const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN;
const keycloakBaseUrl = process.env.REACT_APP_KEYCLOAK_URL;
const keycloakRealm = process.env.REACT_APP_KEYCLOAK_REALM;
const Storage = require('utils/storage');
const PersistenceConfig = require('services/auth-management/persistence-config');

const internals = {};

function recordSsoRuntime(event, details = {}) {

    if (typeof window === 'undefined') {
        return null;
    }

    const entry = internals.sanitizeRuntimeDebug({
        at: new Date().toISOString(),
        event,
        details
    });

    const current = window.__nearpeerSsoDebug || {};
    const recent = [entry].concat(current.recent || []).slice(0, 30);

    window.__nearpeerSsoDebug = {
        ...current,
        ...entry,
        recent
    };

    try {
        Storage.setItem(PersistenceConfig.keys.sso.runtimeDebug, JSON.stringify(recent));
    }
    catch (err) {}

    try {
        window.dispatchEvent(new Event('nearpeer:client-debug:update'));
        window.dispatchEvent(new Event('nearpeer:auth-management-debug:update'));
    }
    catch (err) {}

    return entry;
}


const AUTH0_CLIENT_IDS = {
    production: 'krItgEZRRtQHwtdbdC3pWyeOWeSqFA0i',
    staging: 'HiPsRlmqDVH4YnJAOaIJm7bjZeGmKWR1'
};
const CALLBACK_CONTEXT_STORAGE_KEY = PersistenceConfig.keys.sso.callbackContext;

const createProviderConfig = (config) => ({
    ...config,
    formatLoginMethods(loginMethods) {

        const normalizedLoginMethods = internals.normalizeSingleLoginMethod(loginMethods);

        if (!normalizedLoginMethods) {
            return null;
        }

        return config.formatLoginMethod(normalizedLoginMethods);
    },
    applyLoginMethods({ params, loginMethods }) {

        params.set(config.loginMethodsParam, loginMethods);
    }
});

const PROVIDERS = {
    auth0: createProviderConfig({
        label: 'Auth0',
        domain: auth0Domain,
        clientId: process.env.REACT_APP_AUTH0_CLIENT_ID || null,
        scope: process.env.REACT_APP_AUTH0_SCOPE,
        authorizePath: '/authorize',
        tokenPath: '/oauth/token',
        redirectUris: {
            signup: process.env.REACT_APP_AUTH0_SIGNUP_CALLBACK_URL,
            login: process.env.REACT_APP_AUTH0_LOGIN_CALLBACK_URL
        },
        storageKeys: {
            ...PersistenceConfig.keys.sso.auth0
        },
        getIssuerBase() {

            if (!auth0Domain) {
                return null;
            }

            if (auth0Domain.startsWith('http')) {
                return auth0Domain.replace(/\/$/, '');
            }

            return `https://${auth0Domain}`;
        },
        loginMethodsParam: 'connection',
        formatLoginMethod(loginMethod) {

            if (loginMethod === 'google') {
                return 'google-oauth2';
            }

            return loginMethod;
        }
    }),
    keycloak: createProviderConfig({
        label: 'Keycloak',
        domain: keycloakBaseUrl,
        clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID,
        scope: process.env.REACT_APP_KEYCLOAK_SCOPE || 'openid profile email',
        authorizePath: '/auth',
        tokenPath: '/token',
        redirectUris: {
            signup: process.env.REACT_APP_KEYCLOAK_SIGNUP_CALLBACK_URL,
            login: process.env.REACT_APP_KEYCLOAK_LOGIN_CALLBACK_URL
        },
        storageKeys: {
            ...PersistenceConfig.keys.sso.keycloak
        },
        getIssuerBase() {

            if (!keycloakBaseUrl || !keycloakRealm) {
                return null;
            }

            const normalizedBaseUrl = keycloakBaseUrl.replace(/\/$/, '');
            return `${normalizedBaseUrl}/realms/${keycloakRealm}/protocol/openid-connect`;
        },
        loginMethodsParam: 'kc_idp_hint',
        formatLoginMethod(loginMethod) {

            const brokerAliasMatch = loginMethod.match(/\/broker\/([^/]+)\/endpoint\/?$/);

            if (brokerAliasMatch?.[1]) {
                return brokerAliasMatch[1];
            }

            return loginMethod;
        }
    })
};

module.exports = {
    getSSOProviderConfig,
    parseSSO,
    getSSOProvider,
    getSSOLoginMethods,
    getSSOCallbackUri,
    normalizeSSO,
    getEmailFromClaims,
    getCallbackContext,
    setCallbackContext,
    clearCallbackContext,
    createSessionManager,
    loginWithRedirect,
    handleRedirectCallback,
    getIdTokenClaims,
    clearSession,
    logout,
    decodeJwt,
    randomString,
    validateTokenNonce,
    recordSsoRuntime
};
module.exports.internals = internals;

function getSSOProviderConfig(sso) {

    const provider = getSSOProvider(sso);
    const providerConfig = internals.getProviderConfig(provider);

    if (provider !== 'auth0') {
        return providerConfig;
    }

    return {
        ...providerConfig,
        clientId: providerConfig.clientId || internals.getAuth0ClientId()
    };
}

function parseSSO(sso) {

    const entries = internals.toEntries(sso);
    const prefixedEntries = entries
        .map(internals.parseEntry)
        .filter(({ provider }) => !!provider);

    const provider = prefixedEntries[0]?.provider
        || 'auth0';

    const selectedEntries = prefixedEntries.length
        ? prefixedEntries.filter((entry) => entry.provider === provider)
        : entries.map((value) => ({
            provider,
            raw: value,
            value
        }));

    return {
        provider,
        entries: selectedEntries,
        loginMethods: selectedEntries.map(({ value }) => value).join(',')
    };
}

function getSSOProvider(sso) {

    return parseSSO(sso).provider;
}

function getSSOLoginMethods(sso) {

    return parseSSO(sso).loginMethods;
}

function getSSOCallbackUri({ provider, flow }) {

    const providerConfig = internals.getProviderConfig(provider);
    return internals.normalizeCallbackUri(providerConfig.redirectUris[flow === 'signup' ? 'signup' : 'login']);
}

function normalizeSSO(rawSSO) {

    if (typeof rawSSO !== 'string') {
        return rawSSO;
    }

    const sso = rawSSO.trim();

    if (!sso) {
        return null;
    }

    if (sso.startsWith('keycloak-') || sso.startsWith('auth0-')) {
        return sso;
    }

    return `auth0-${sso}`;
}

function getEmailFromClaims(claims) {

    if (!claims || typeof claims !== 'object') {
        return null;
    }

    const directEmail = internals.normalizeEmail(claims.email);

    if (directEmail) {
        return directEmail;
    }

    const preferredUsername = internals.normalizeEmail(claims.preferred_username);

    if (preferredUsername) {
        return preferredUsername;
    }

    const upn = internals.normalizeEmail(claims.upn);

    if (upn) {
        return upn;
    }

    if (Array.isArray(claims.emails)) {
        return claims.emails
            .map(internals.normalizeEmail)
            .find(Boolean) || null;
    }

    return internals.normalizeEmail(claims.emails);
}

internals.normalizeCallbackHost = (host) => host.replace(/^www\./, '');
internals.getProviderConfig = (provider) => PROVIDERS[provider] || PROVIDERS.auth0;

internals.getNormalizedHostFromUrl = (value) => {

    if (typeof value !== 'string' || !value) {
        return null;
    }

    try {
        return internals.normalizeCallbackHost(new URL(value).hostname);
    }
    catch (err) {
        return null;
    }
};

internals.getCurrentHostname = () => {

    if (typeof window === 'undefined' || !window.location?.hostname) {
        return null;
    }

    return internals.normalizeCallbackHost(window.location.hostname);
};

internals.getAuth0Hosts = () => {

    const configuredHosts = [
        process.env.REACT_APP_AUTH0_LOGIN_CALLBACK_URL,
        process.env.REACT_APP_AUTH0_SIGNUP_CALLBACK_URL
    ]
        .map(internals.getNormalizedHostFromUrl)
        .filter(Boolean);
    const currentHostname = internals.getCurrentHostname();

    return currentHostname
        ? configuredHosts.concat(currentHostname)
        : configuredHosts;
};

internals.isProductionAuth0Host = (host) => (
    host === 'teams.nearpeer.net'
    || host === 'nearpeer.net'
    || host === 'www.nearpeer.net'
);

internals.resolveAuth0Environment = () => {

    const hosts = internals.getAuth0Hosts();

    if (hosts.some((host) => host.includes('staging.nearpeer.net'))) {
        return 'staging';
    }

    if (hosts.some(internals.isProductionAuth0Host)) {
        return 'production';
    }

    if (process.env.APP_FLAVOUR === 'teams' || process.env.NODE_ENV === 'production') {
        return 'production';
    }

    return 'staging';
};

internals.getAuth0ClientId = () => {

    if (process.env.REACT_APP_AUTH0_CLIENT_ID) {
        return process.env.REACT_APP_AUTH0_CLIENT_ID;
    }

    return AUTH0_CLIENT_IDS[internals.resolveAuth0Environment()] || null;
};

internals.normalizeEmail = (value) => {

    if (typeof value !== 'string') {
        return null;
    }

    const email = value.trim().toLowerCase();

    if (!email || !email.includes('@')) {
        return null;
    }

    return email;
};

function getCallbackContext() {

    const callbackContext = Storage.get(CALLBACK_CONTEXT_STORAGE_KEY);

    if (!callbackContext || typeof callbackContext !== 'object') {
        return null;
    }

    return {
        ...callbackContext,
        sso: normalizeSSO(callbackContext.sso)
    };
}

function setCallbackContext(callbackContext) {

    if (!callbackContext || typeof callbackContext !== 'object') {
        return;
    }

    Storage.set(CALLBACK_CONTEXT_STORAGE_KEY, {
        ...callbackContext,
        sso: normalizeSSO(callbackContext.sso)
    });
}

function clearCallbackContext() {

    Storage.remove(CALLBACK_CONTEXT_STORAGE_KEY);
}
internals.normalizeCallbackUri = (callbackUri) => {

    if (!callbackUri || typeof window === 'undefined' || window.cordova) {
        return callbackUri;
    }

    try {
        const configuredUri = new URL(callbackUri);

        // Strip a leading www from callback hosts so the redirect target stays on the
        // canonical host. This is needed for teams SSO because www/non-www origins
        // do not share browser storage.
        configuredUri.hostname = internals.normalizeCallbackHost(configuredUri.hostname);

        return configuredUri.toString();
    }
    catch (err) {
        return callbackUri;
    }
};

function createSessionManager({ storageKeys }) {

    const readStorageValue = (key) => {

        // Must use localStorage here because SSO callback launches can lose sessionStorage state.
        return Storage.getItem(key);
    };

    const writeStorageValue = (key, value) => {

        Storage.setItem(key, value);
    };

    const removeStorageValue = (key) => {

        Storage.removeItem(key);
    };

    return {
        setRedirectRequest({ state, nonce, redirectUri }) {

            writeStorageValue(storageKeys.state, state);
            writeStorageValue(storageKeys.nonce, nonce);
            writeStorageValue(storageKeys.redirectUri, redirectUri);
        },

        getRedirectRequest() {

            return {
                state: readStorageValue(storageKeys.state),
                nonce: readStorageValue(storageKeys.nonce),
                redirectUri: readStorageValue(storageKeys.redirectUri)
            };
        },

        setTokenResponse(tokenResponse) {

            writeStorageValue(storageKeys.tokenResponse, JSON.stringify(tokenResponse));
        },

        getTokenResponse() {

            const tokenResponse = readStorageValue(storageKeys.tokenResponse);

            if (!tokenResponse) {
                return null;
            }

            return JSON.parse(tokenResponse);
        },

        clearSession() {

            removeStorageValue(storageKeys.state);
            removeStorageValue(storageKeys.nonce);
            removeStorageValue(storageKeys.redirectUri);
            removeStorageValue(storageKeys.tokenResponse);
        }
    };
}

async function loginWithRedirect({
    issuerBase,
    clientId,
    scope,
    authorizePath,
    missingConfigurationMessage = 'Missing SSO issuer configuration',
    authorizationParams,
    beforeOpenUrl,
    redirectRequestCreatedHandler,
    openUrl,
    sessionManager,
    applyProviderLoginMethods
}) {

    if (!issuerBase || !clientId) {
        const error = new Error(internals.getMissingConfigurationMessage({
            authorizePath,
            clientId,
            issuerBase,
            missingConfigurationMessage
        }));

        error.code = 'SSO_MISSING_CONFIGURATION';
        error.details = {
            hasIssuerBase: !!issuerBase,
            hasClientId: !!clientId,
            authorizePath: authorizePath || null
        };

        recordSsoRuntime('sso_missing_configuration', {
            message: error.message,
            details: error.details
        });

        throw error;
    }

    const redirectRequest = internals.createRedirectRequest(authorizationParams?.redirect_uri);
    const {
        state,
        nonce,
        redirectUri
    } = redirectRequest;

    sessionManager.setRedirectRequest(redirectRequest);

    if (typeof redirectRequestCreatedHandler === 'function') {
        redirectRequestCreatedHandler(redirectRequest);
    }

    const params = internals.createAuthorizeParams({
        applyProviderLoginMethods,
        authorizationParams,
        clientId,
        nonce,
        redirectUri,
        scope,
        state
    });

    const loginUrl = `${issuerBase}${authorizePath}?${params.toString()}`;

    recordSsoRuntime('sso_authorize_url_created', {
        issuerBase,
        authorizePath,
        clientIdPresent: !!clientId,
        redirectUri,
        providerLoginMethods: authorizationParams?.loginMethods || null,
        userEmailPresent: !!authorizationParams?.userEmail,
        isCordova: !!window.cordova,
        willUseCordovaBrowser: !!openUrl
    });

    if (openUrl) {
        if (typeof beforeOpenUrl === 'function') {
            beforeOpenUrl({
                issuerBase,
                authorizePath,
                redirectUri,
                loginUrl,
                loginMethods: authorizationParams?.loginMethods || null,
                userEmail: authorizationParams?.userEmail || null
            });
        }

        openUrl(loginUrl);
        return;
    }

    window.location = loginUrl;
}

async function handleRedirectCallback({
    issuerBase,
    clientId,
    tokenPath,
    exchangeErrorMessage = 'Failed to exchange SSO authorization code',
    redirectRequestFallback,
    sessionManager,
    callbackUrl
}) {

    const queryParams = internals.getCallbackSearchParams(callbackUrl);
    const code = queryParams.get('code');
    const responseState = (queryParams.get('state') || '').replace(/#$/, '');
    const storedRedirectRequest = sessionManager.getRedirectRequest();
    const {
        state: savedState,
        nonce: savedNonce,
        redirectUri
    } = internals.resolveRedirectRequest({
        responseState,
        storedRedirectRequest,
        redirectRequestFallback
    });

    if (!code || !responseState || !savedState || responseState !== savedState) {
        throw new Error('Invalid SSO redirect state');
    }

    const tokenUrl = `${issuerBase}${tokenPath}`;

    recordSsoRuntime('sso_token_exchange_start', {
        tokenUrl,
        tokenPath,
        clientIdPresent: !!clientId,
        redirectUriPresent: !!redirectUri,
        callbackUrlPresent: !!callbackUrl,
        codePresent: !!code,
        responseStatePresent: !!responseState,
        savedStatePresent: !!savedState,
        stateMatches: !!(responseState && savedState && responseState === savedState)
    });

    const body = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: clientId,
        code,
        redirect_uri: redirectUri
    });

    const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: body.toString()
    });

    if (!response.ok) {
        const errorBody = await response.text();
        console.error('[sso] token exchange failed', {
            issuerBase,
            tokenPath,
            status: response.status,
            statusText: response.statusText,
            errorBody
        });

        recordSsoRuntime('sso_token_exchange_failed', {
            tokenUrl,
            status: response.status,
            statusText: response.statusText,
            errorBody
        });

        throw new Error(exchangeErrorMessage);
    }

    const tokenResponse = await response.json();

    recordSsoRuntime('sso_token_exchange_success', {
        tokenUrl,
        hasIdToken: !!tokenResponse.id_token,
        hasAccessToken: !!tokenResponse.access_token
    });

    validateTokenNonce({
        expectedNonce: savedNonce,
        idToken: tokenResponse.id_token
    });

    sessionManager.setTokenResponse(tokenResponse);

    return tokenResponse;
}

async function getIdTokenClaims(sessionManager) {

    const tokenResponse = sessionManager.getTokenResponse();

    if (!tokenResponse) {
        return null;
    }

    return {
        ...decodeJwt(tokenResponse.id_token),
        __raw: tokenResponse.id_token
    };
}

async function clearSession(sessionManager) {

    sessionManager.clearSession();
}

async function logout(sessionManager) {

    sessionManager.clearSession();
}

function randomString() {

    const randomBytes = new Uint8Array(32);
    window.crypto.getRandomValues(randomBytes);

    return Array.from(randomBytes)
        .map((value) => value.toString(16).padStart(2, '0'))
        .join('');
}

internals.getMissingConfigurationMessage = ({
    authorizePath,
    clientId,
    issuerBase,
    missingConfigurationMessage
}) => {

    const missingFields = [];

    if (!issuerBase) {
        missingFields.push('issuerBase');
    }

    if (!clientId) {
        missingFields.push('clientId');
    }

    if (!missingFields.length) {
        return missingConfigurationMessage;
    }

    return `${missingConfigurationMessage}: missing ${missingFields.join(', ')}${authorizePath ? ` for ${authorizePath}` : ''}`;
};

internals.createRedirectRequest = (redirectUri) => ({
    state: randomString(),
    nonce: randomString(),
    redirectUri
});

internals.createAuthorizeParams = ({
    applyProviderLoginMethods,
    authorizationParams,
    clientId,
    nonce,
    redirectUri,
    scope,
    state
}) => {

    const params = new URLSearchParams({
        response_type: 'code',
        client_id: clientId,
        redirect_uri: redirectUri,
        scope,
        state,
        nonce
    });

    if (authorizationParams?.userEmail) {
        params.set('login_hint', authorizationParams.userEmail);
    }

    if (authorizationParams?.prompt) {
        params.set('prompt', authorizationParams.prompt);
    }

    if (authorizationParams?.max_age !== undefined && authorizationParams?.max_age !== null) {
        params.set('max_age', authorizationParams.max_age);
    }

    if (authorizationParams?.acr_values) {
        params.set('acr_values', authorizationParams.acr_values);
    }

    if (authorizationParams?.loginMethods && typeof applyProviderLoginMethods === 'function') {
        applyProviderLoginMethods({
            params,
            loginMethods: authorizationParams.loginMethods
        });
    }

    return params;
};

function decodeJwt(token) {

    if (!token) {
        return {};
    }

    const payload = token.split('.')[1];

    if (!payload) {
        return {};
    }

    return JSON.parse(window.atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
}

function validateTokenNonce({ expectedNonce, idToken }) {

    if (!expectedNonce) {
        throw new Error('Missing stored SSO nonce');
    }

    const decodedToken = decodeJwt(idToken);

    if (!decodedToken?.nonce || decodedToken.nonce !== expectedNonce) {
        throw new Error('Invalid SSO nonce');
    }
}

internals.toEntries = (sso) => {

    if (typeof sso !== 'string') {
        return [];
    }

    return sso
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);
};

internals.parseEntry = (entry) => {

    const match = entry.match(/^(auth0|keycloak)-(.+)$/i);

    if (!match) {
        return {
            provider: null,
            raw: entry,
            value: entry
        };
    }

    return {
        provider: match[1].toLowerCase(),
        raw: entry,
        value: match[2]
    };
};

internals.normalizeSingleLoginMethod = (loginMethods) => {

    if (!loginMethods || loginMethods.includes(',')) {
        return null;
    }

    return loginMethods.trim();
};

internals.resolveRedirectRequest = ({
    responseState,
    storedRedirectRequest,
    redirectRequestFallback
}) => {

    const candidates = [storedRedirectRequest, redirectRequestFallback]
        .filter((candidate) => candidate?.state || candidate?.nonce || candidate?.redirectUri);
    const matchedCandidate = candidates.find((candidate) => candidate?.state === responseState);

    if (matchedCandidate) {
        return matchedCandidate;
    }

    if (storedRedirectRequest?.state) {
        return storedRedirectRequest;
    }

    return redirectRequestFallback || {
        state: null,
        nonce: null,
        redirectUri: null
    };
};

internals.getCallbackSearchParams = (callbackUrl) => {

    if (callbackUrl) {
        try {
            return new URL(callbackUrl).searchParams;
        }
        catch (err) {
            console.error('[sso] invalid callbackUrl, falling back to window.location.search', {
                callbackUrl,
                error: err?.message || null
            });
        }
    }

    return new URLSearchParams(window.location.search);
};


internals.sanitizeRuntimeDebug = (value) => {

    try {
        return JSON.parse(JSON.stringify(value, (key, innerValue) => {

            if ([
                'token',
                'access_token',
                'id_token',
                'refresh_token',
                '__raw',
                'code'
            ].includes(key)) {
                return innerValue ? '[redacted]' : innerValue;
            }

            return innerValue;
        }));
    }
    catch (err) {
        return {
            at: new Date().toISOString(),
            event: 'sso_debug_sanitize_failed',
            details: {
                message: err?.message || String(err)
            }
        };
    }
};
